import { EmoticonId, getAllEmoticonIds } from '@common/data/Emoticons';
import { Container, Sprite } from 'pixi.js';

import { AssetManager } from '../AssetManager';
import { ClientServiceContainer } from './ClientServiceContainer';

interface EmoticonInstance {
  sprite: Sprite;
  timer: number;
  duration: number;
}

const EMOTICON_DURATION = 1.5; // seconds
const EMOTICON_FLOAT_HEIGHT = 16; // pixels
const EMOTICON_Z_INDEX = 1000000;

/**
 * Service that renders transient emoticons (emojis) above entities.
 * Manages the PIXI container and animation loop for emoticons.
 */
export class EmoticonService {
  private container: Container | null = null;
  private emoticons: Map<number, EmoticonInstance[]> = new Map();
  private services: ClientServiceContainer;
  // Cache of spritesheet load requests to avoid duplicate requests
  private requestedSpritesheets: Set<string> = new Set();

  constructor(services: ClientServiceContainer) {
    this.services = services;
  }

  public setContainer(container: Container): void {
    this.container = container;
  }

  public showEmoticon(entityId: number, emoticonId: EmoticonId): void {
    if (!this.container) return;

    // Verify entity exists
    const world = this.services.clientWorld.getWorld();
    if (!world.getEntity(entityId)) return;

    const spritesheetService = this.services.spritesheet;

    // Ensure spritesheet is loaded
    if (!spritesheetService.isLoaded(emoticonId)) {
      if (!this.requestedSpritesheets.has(emoticonId)) {
        this.requestedSpritesheets.add(emoticonId);
        spritesheetService.loadSpritesheet(emoticonId).catch((err) => {
          console.error(`[EmoticonService] Failed to load spritesheet '${emoticonId}':`, err);
        });
      }
      return;
    }

    // Create the sprite
    // Emoticons are currently single-frame spritesheets, so they follow the {id}_1 pattern
    const textureId = `${emoticonId}_1`;
    const assetManager = AssetManager.getInstance();
    if (!assetManager.hasTexture(textureId)) {
      return;
    }

    const texture = assetManager.getTexture(textureId);
    const sprite = new Sprite(texture);

    sprite.anchor.set(0.5, 1);
    sprite.zIndex = EMOTICON_Z_INDEX;
    this.container.addChild(sprite);

    const instance: EmoticonInstance = {
      sprite,
      timer: 0,
      duration: EMOTICON_DURATION,
    };

    const instances = this.emoticons.get(entityId) || [];
    instances.push(instance);
    this.emoticons.set(entityId, instances);

    // Initial position
    this.updateSpritePosition(entityId, instance);
  }

  public update(deltaTime: number): void {
    if (!this.container) return;

    // Update existing emoticons
    for (const [entityId, instances] of this.emoticons.entries()) {
      const world = this.services.clientWorld.getWorld();
      const entityExists = !!world.getEntity(entityId);

      for (let i = instances.length - 1; i >= 0; i--) {
        const instance = instances[i];
        instance.timer += deltaTime;

        if (instance.timer >= instance.duration || !entityExists) {
          instance.sprite.destroy();
          instances.splice(i, 1);
          continue;
        }

        // Float up and fade out
        const progress = instance.timer / instance.duration;
        instance.sprite.alpha = 1 - progress;

        this.updateSpritePosition(entityId, instance, progress);
      }

      if (instances.length === 0) {
        this.emoticons.delete(entityId);
      }
    }
  }

  private updateSpritePosition(
    entityId: number,
    instance: EmoticonInstance,
    progress: number = 0,
  ): void {
    const world = this.services.clientWorld.getWorld();
    const transform = world.getComponent(entityId, 'Transform');
    const visPos = world.getComponent(entityId, 'VisualPosition');
    const collider = world.getComponent(entityId, 'Collider');

    if (!transform) return;

    const x = visPos ? visPos.x : transform.x;
    const y = visPos ? visPos.y : transform.y;

    // Center X
    let centerXOffset = 0;
    if (collider) {
      centerXOffset = collider.offsetX + collider.width / 2;
    }

    // Top Y
    let topYOffset = -4; // Base padding
    if (collider) {
      topYOffset = collider.offsetY - 4;
    }

    const floatOffset = progress * EMOTICON_FLOAT_HEIGHT;
    instance.sprite.position.set(x + centerXOffset, y + topYOffset - floatOffset);
  }

  /**
   * Pre-load all registered emoticons.
   */
  public async loadAll(): Promise<void> {
    const emoticonIds = getAllEmoticonIds();
    const loadPromises = emoticonIds.map((id) => this.services.spritesheet.loadSpritesheet(id));
    await Promise.all(loadPromises);
  }
}
